import { joinClassNames } from '@vertex-protocol/web-common';
import { Icons } from '@vertex-protocol/web-ui';
import { useCallback, useState } from 'react';

interface Props {
  message: string;
  details?: string;
}

/**
 * Inline error message shown below the submit button.
 * Includes a copy-to-clipboard button so users can easily report bugs.
 */
export function NadoDepositError({ message, details }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const text = details || message;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      console.error('Failed to copy to clipboard');
    }
  }, [details, message]);

  return (
    <div
      className={joinClassNames(
        'bg-negative/10 border-negative/30 rounded-sm border px-3 py-2',
        'flex items-start gap-x-2',
      )}
    >
      <Icons.Warning className="text-negative mt-0.5 size-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-negative text-xs font-medium">{message}</p>
        {details && details !== message && (
          <p className="text-text-tertiary mt-1 break-words text-2xs">
            {details}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className={joinClassNames(
          'text-text-tertiary hover:text-text-secondary flex-shrink-0 transition-colors',
          'flex items-center gap-x-1 text-2xs',
        )}
        title="Copy error details to clipboard"
      >
        {copied ? (
          <>
            <Icons.Check className="size-3" />
            Copied
          </>
        ) : (
          <>
            <Icons.Copy className="size-3" />
            Copy
          </>
        )}
      </button>
    </div>
  );
}
